import { action, observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import type React from 'react';
import '@zinc/ui/original-src/themes/reshaped/theme.css';
import { Reshaped, Theme } from '@zinc/ui';
import useComputed from 'bz-utilities/hooks/computed';
import useConst from 'bz-utilities/hooks/const';
import createContext from 'bz-utilities/hooks/context-v2';
import useUnmountEffect from 'bz-utilities/hooks/unmount-effect';
import { baseThemeDefinition, generateThemeColors, getThemeCSS } from 'reshaped/themes';
import colors from 'tailwindcss/colors';

export interface IThemeValues {
  accent: string;
  primary: string;
  secondary: string;
}

export interface ITheme extends IThemeValues {
  accent: string;
  id: number;
  name: string;
  primary: string;
  secondary: string;
  reshapedTheme: Record<string, any>;
}

export const [BzThemeStoreContext, useBzThemeStore] = createContext<BzThemeStore>('BzThemeStore');

export const BzThemeStoreProvider: React.FC<React.PropsWithChildren> = observer((props) => {
  const store = useConst(() => new BzThemeStore());

  const css = useComputed(
    () =>
      getThemeCSS(
        'theme',
        {
          ...baseThemeDefinition,
          ...store.theme.reshapedTheme
        },
        { colorContrastAlgorithm: 'apca' }
      ),
    []
  );

  useUnmountEffect(() => {
    store.reshapedThemeBuilderDisposers.forEach((disposer) => disposer());
  });

  return (
    <BzThemeStoreContext.Provider value={store}>
      <Reshaped>
        {css && <style>{css}</style>}
        <Theme name="theme">{props.children}</Theme>
      </Reshaped>
    </BzThemeStoreContext.Provider>
  );
});

BzThemeStoreProvider.displayName = 'BzThemeStoreProvider';

class BzThemeStore {
  @observable theme: Partial<ITheme> = {
    reshapedTheme: {
      color: {
        ...generateThemeColors({ primary: colors.blue[600] })
      }
    }
  };

  reshapedThemeBuilderDisposers = [
    reaction(() => this.theme.id, this.generate),
    reaction(() => this.theme.primary, this.generate),
    reaction(() => this.theme.accent, this.generate)
  ];

  @action.bound
  generate() {
    if (this.theme.id !== -1) return;

    const primary = this.theme.primary as keyof typeof colors;
    const accent = this.theme.accent as keyof typeof colors;
    this.theme.reshapedTheme = {
      color: {
        ...generateThemeColors({ primary: colors[primary][600] }),
        sidebarBackground: { hex: colors[accent][700], darkHex: colors[accent][700] },
        sidebarBackgroundActive: { hex: colors[accent][800], darkHex: colors[accent][800] },
        sidebarBackgroundHover: { hex: colors[accent][600], darkHex: colors[accent][600] },
        sidebarIcon: { hex: colors[accent][300], darkHex: colors[accent][300] },
        sidebarText: { hex: colors[accent][100], darkHex: colors[accent][100] },
        backgroundPage: { hex: '#f8fafc', darkHex: '#f8fafc' }
      }
    };
  }
}

export default BzThemeStore;
